import Modal from "@mui/material/Modal";
import { useState, useEffect } from "react";
import axiosClient from "../axiosClient";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { SketchPicker } from "react-color";
import {
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
  useMediaQuery,
} from "@mui/material";

const style = {
  position: "absolute",
  top: { xs: "50%", sm: "50%" },
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "80vw", md: 800 },
  maxHeight: { xs: "90vh", sm: "85vh", md: "600px" },
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  overflowY: "auto",
  borderRadius: 2,
  maxWidth: "100%",
};

export default function UserEditModal({ id, open, handleClose, getUsers }) {
  const [formData, setFormData] = useState({});
  const [role, setRole] = useState("");
  const [gender, setGender] = useState("");
  const [color, setColor] = useState("");
  const [errors, setErrors] = useState({});
  const isMobile = useMediaQuery("(max-width:600px)");

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  useEffect(() => {
    if (id) {
      axiosClient
        .get(`api/user/${id}/`)
        .then((res) => {
          const data = res?.data;
          setFormData({
            ...data,
            hardcopy_consent: !!data.hardcopy_consent,
          });
          setGender(res?.data?.gender);
          setRole(res?.data?.user_role);
          setColor(res?.data?.color || "");
          setErrors({});
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [open, id]);

  const validateForm = () => {
    let localErrors = {};

    if (!formData.first_name || formData.first_name.length < 2) {
      localErrors.first_name = "First name must be at least 2 characters";
    }

    if (!formData.last_name || formData.last_name.length < 2) {
      localErrors.last_name = "Last name must be at least 2 characters";
    }

    if (!formData.phone_number || formData.phone_number.trim() === "") {
      localErrors.phone_number = "Phone number is required";
    }

    if (!formData.email) {
      localErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      localErrors.email = "Invalid email format";
    }

    setErrors(localErrors);
    return Object.keys(localErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      ...formData,
      gender: gender,
      color: color,
    };

    axiosClient
      .patch(`api/user-edit/${id}/`, payload)
      .then((res) => {
        getUsers();
        handleClose();
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.data) {
          setErrors(error.response.data);
        }
      });
  };

  // Handle modal close events
  const handleModalClose = (event, reason) => {
    // Prevent closing on backdrop click or Escape key press
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      role="dialog"
      aria-modal="true"
      disableEscapeKeyDown
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            id="modal-modal-title"
            sx={{ fontSize: { xs: "1.1rem", sm: "1.4rem", md: "1.5rem" }, fontWeight: "500" }}
            variant="h4"
            component="h2"
          >
            Edit User
          </Typography>
          <Typography
            onClick={handleClose}
            sx={{
              cursor: "pointer",
              p: { xs: 0.5, sm: 1 },
              "& svg": { fontSize: { xs: "1.4rem", sm: "1.8rem" } },
            }}
            id="modal-modal-close"
            variant="h6"
            component="span"
            role="button"
            aria-label="Close modal"
          >
            <CloseOutlinedIcon />
          </Typography>
        </Box>
        <hr
          style={{
            border: "none",
            borderBottom: "1px solid #e6ebf1",
            marginBottom: { xs: "1rem", sm: "1.5rem" },
          }}
        />
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2, pt: { xs: 1, sm: 2 } }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  name="first_name"
                  onChange={handleChange}
                  value={formData?.first_name || ""}
                  InputLabelProps={{
                    shrink: true,
                    required: true,
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                    "& .MuiInputBase-input": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                  }}
                />
                {errors?.first_name && (
                  <Typography
                    sx={{ textAlign: "left", mt: 0.5, fontSize: "0.8rem" }}
                    color="error"
                    variant="body2"
                  >
                    {errors.first_name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Surname"
                  variant="outlined"
                  name="last_name"
                  onChange={handleChange}
                  value={formData?.last_name || ""}
                  InputLabelProps={{
                    shrink: true,
                    required: true,
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                    "& .MuiInputBase-input": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                  }}
                />
                {errors?.last_name && (
                  <Typography
                    sx={{ textAlign: "left", mt: 0.5, fontSize: "0.8rem" }}
                    color="error"
                    variant="body2"
                  >
                    {errors.last_name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  name="date_birth"
                  value={formData?.date_birth || ""}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                    "& .MuiInputBase-input": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                  }}
                />
                {errors?.date_birth && (
                  <Typography
                    sx={{ textAlign: "left", mt: 0.5, fontSize: "0.8rem" }}
                    color="error"
                    variant="body2"
                  >
                    {errors.date_birth}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                {/* <TextField
                fullWidth
                label="Address"
                variant="outlined"
                name="address"
                onChange={handleChange}
                value={formData?.address}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Postal code"
                variant="outlined"
                name="postcode"
                onChange={handleChange}
                value={formData?.postcose}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                name="city"
                onChange={handleChange}
                value={formData?.city}
                InputLabelProps={{
                  shrink: true,
                }}
              /> */}
                <TextField
                  fullWidth
                  label="Telephone"
                  variant="outlined"
                  name="phone_number"
                  onChange={handleChange}
                  value={formData?.phone_number || ""}
                  InputLabelProps={{
                    shrink: true,
                    required: true,
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                    "& .MuiInputBase-input": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                  }}
                />
                {errors?.phone_number && (
                  <Typography
                    sx={{ textAlign: "left", mt: 0.5, fontSize: "0.8rem" }}
                    color="error"
                    variant="body2"
                  >
                    {errors.phone_number}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                {/* <TextField
                fullWidth
                label="ID Number"
                variant="outlined"
                name="id_card"
                onChange={handleChange}
                value={formData?.id_card}
                InputLabelProps={{
                  shrink: true,
                }}
              /> */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <FormLabel
                    component="legend"
                    sx={{ mr: 2, fontSize: { xs: "0.9rem", sm: "0.95rem" } }}
                  >
                    Gender
                  </FormLabel>
                  <FormControl component="fieldset">
                    <RadioGroup
                      name="gender"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      sx={{ flexDirection: "row" }}
                    >
                      <FormControlLabel
                        value="M"
                        control={<Radio size="small" />}
                        label="Male"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.9rem", sm: "0.95rem" } } }}
                      />
                      <FormControlLabel
                        value="F"
                        control={<Radio size="small" />}
                        label="Female"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.9rem", sm: "0.95rem" } } }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                {errors?.gender && (
                  <Typography
                    sx={{ textAlign: "left", mb: 1, fontSize: "0.8rem" }}
                    color="error"
                    variant="body2"
                  >
                    {errors.gender}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  type="email"
                  name="email"
                  onChange={handleChange}
                  value={formData?.email || ""}
                  InputLabelProps={{
                    shrink: true,
                    required: true,
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                    "& .MuiInputBase-input": { fontSize: { xs: "0.9rem", sm: "0.95rem" } },
                  }}
                />
                {errors?.email && (
                  <Typography
                    sx={{ textAlign: "left", mt: 0.5, fontSize: "0.8rem" }}
                    color="error"
                    variant="body2"
                  >
                    {errors.email}
                  </Typography>
                )}
              </Grid>
              {role === "STAFF" && (
                <Grid item xs={12}>
                  <Typography
                    sx={{ textAlign: "left", mt: 1.5, mb: 1, fontSize: { xs: "0.9rem", sm: "0.95rem" } }}
                    variant="body1"
                    gutterBottom
                  >
                    Pick a color for the appointment:
                  </Typography>
                  {isMobile ? (
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      style={{ width: "100%", height: "40px", borderRadius: "4px" }}
                    />
                  ) : (
                    <Box sx={{ width: { sm: "200px", md: "250px" }, maxWidth: "100%" }}>
                      <SketchPicker
                        color={color}
                        onChangeComplete={(newColor) => setColor(newColor.hex)}
                        styles={{
                          default: {
                            picker: {
                              width: "100%",
                              boxShadow: "none",
                              borderRadius: "4px",
                              padding: "8px",
                              fontSize: "0.9rem",
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, mb: 2 }}>
                  <FormLabel
                    component="legend"
                    sx={{ mr: 2, fontSize: { xs: "0.9rem", sm: "0.95rem" } }}
                  >
                    Hardcopy Consent
                  </FormLabel>
                  <FormControl component="fieldset">
                    <RadioGroup
                      name="hardcopy_consent"
                      value={formData?.hardcopy_consent ? "true" : "false"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hardcopy_consent: e.target.value === "true",
                        })
                      }
                      sx={{ flexDirection: "row" }}
                    >
                      <FormControlLabel
                        value="true"
                        control={<Radio size="small" />}
                        label="Yes"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.9rem", sm: "0.95rem" } } }}
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio size="small" />}
                        label="No"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: { xs: "0.9rem", sm: "0.95rem" } } }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: "0.9rem", sm: "0.95rem" },
              borderRadius: "8px",
            }}
          >
            Submit
          </Button>
        </form>
      </Box>
    </Modal>
  );
}